import { DISPLAY_DATE_VARIANTS, formatDateForDisplay } from '@shared/utils/dateUtils';

export const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

export const formatDuration = (durationMs) => {
  if (durationMs == null || Number.isNaN(Number(durationMs))) return null;
  const ms = Number(durationMs);
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

export const formatCallbackTimestamp = (callback) => (
  formatDateForDisplay(callback?.payload?.metadata?.timestamp, { variant: DISPLAY_DATE_VARIANTS.LOG })
  || '----/--/-- --:--:--'
);

export const buildCallbackSummary = (callback) => {
  const payload = callback?.payload ?? {};
  const parts = [
    `step=${payload.step ?? '?'}`,
    `status=${payload.status ?? '?'}`,
  ];

  const latency = payload.metadata?.latency_ms;
  if (latency != null) {
    parts.push(`latency_ms=${latency}`);
  }

  if (callback?.event_type === 'TASK_COMPLETED') {
    const artifactsCount = Array.isArray(payload.output_artifacts) ? payload.output_artifacts.length : 0;
    parts.push(`artifacts=${artifactsCount}`);
  }

  if (callback?.event_type === 'TASK_FAILED') {
    parts.push(`code=${payload.reason?.code ?? 'N/A'}`);
    parts.push(`recoverable=${payload.reason?.recoverable}`);
  }

  return parts.join(' · ');
};

const formatCount = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const HTTP_METHOD_TAG_COLORS = {
  GET: 'blue',
  POST: 'green',
  PUT: 'orange',
  PATCH: 'gold',
  DELETE: 'red',
};

export const describeHttpLogEntry = (value) => {
  if (Array.isArray(value)) {
    return {
      tag: 'list',
      tagColor: 'purple',
      summary: formatCount(value.length, 'item'),
    };
  }

  if (!value || typeof value !== 'object') {
    return {
      tag: typeof value,
      tagColor: 'default',
      summary: String(value),
    };
  }

  const method = value.method || value.request?.method;
  const url = value.url || value.request?.url;
  const statusCode = value.response?.status ?? value.response?.status_code;
  const service = value.service;

  if (method || url || statusCode != null) {
    const methodTag = method ? String(method).toUpperCase() : 'http';
    const parts = [];
    if (url) parts.push(url);
    if (statusCode != null) parts.push(`→ ${statusCode}`);
    return {
      tag: methodTag,
      tagColor: HTTP_METHOD_TAG_COLORS[methodTag] || 'geekblue',
      summary: parts.join(' ') || undefined,
    };
  }

  if (service) {
    return {
      tag: 'object',
      tagColor: 'geekblue',
      summary: String(service),
    };
  }

  return {
    tag: 'object',
    tagColor: 'geekblue',
    summary: formatCount(Object.keys(value).length, 'field'),
  };
};
